import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { gungAddRemoveSpinner, GungNotificationService, OptionsListMulti } from 'gung-common';
import { first } from 'rxjs';
import { HubspotContacts } from '../../models/hubspot-contacts';
import { GungModalService } from '../../services/gung-modal/gung-modal.service';
import { HubspotContactService } from '../../services/hubspot-contact/hubspot-contact.service';

export interface OptionsListMultiExtended extends OptionsListMulti {
  extra: { [s: string]: any }
}

@Component({
  selector: 'lib-hubspot-create-contact-modal',
  templateUrl: './hubspot-create-contact-modal.component.html',
  styleUrl: './hubspot-create-contact-modal.component.scss',
  standalone: false
})
export class HubspotCreateContactModalComponent implements OnInit {
  protected fb = inject(FormBuilder);
  public activeModal = inject(NgbActiveModal);
  protected hubspotContactService = inject(HubspotContactService);
  protected gungNotificationService = inject(GungNotificationService);

  delegate: GungModalService;
  customerId: string;
  contactForm!: FormGroup;
  componentId: string;
  editContact: HubspotContacts;
  public hubspotPreferences: OptionsListMultiExtended[] = [];
  hubspotPreferencesSelected: string[] = [];
  defaultHubspotPreferences: string[] = [];

  ngOnInit(): void {
    this.componentId = HubspotCreateContactModalComponent.name;
    this.initForm();

    if (this.editContact) {
      // When editing, show the actual communication preferences from the contact
      const contactPrefs = this.editContact?.extra?.communicationPreferences ?? [];
      this.hubspotPreferences = contactPrefs.map(cp => ({
        id: cp.subscriptionId,
        name: cp.subscriptionName,
        extra: cp,
        selected: cp.status === 'SUBSCRIBED'
      }));
    } else {
      // When creating, fetch available optional subscriptions
      this.hubspotContactService
        .getAvailableCommunicationPreferences()
        .pipe(first())
        .subscribe(preferences => {
          if (preferences?.length) {
            this.hubspotPreferences = preferences
              .filter(p => p.isActive && !p.isDefault)
              .map(p => ({
                id: p.id,
                name: p.name,
                extra: p,
                selected: false
              }));

            this.defaultHubspotPreferences = preferences
              .filter(p => p.isActive && p.isDefault)
              .map(p => p.id);
          }
        });
    }
  }

  initForm(): void {
    let contact;
    if (this.editContact) {
      contact = {
        email: this.editContact.extra.hubspotContact.properties.email,
        firstname: this.editContact.extra.hubspotContact.properties.firstname,
        lastname: this.editContact.extra.hubspotContact.properties.lastname,
        phone: this.editContact.extra.hubspotContact.properties.phone
      };
    }
    this.contactForm = this.fb.group({
      email: [contact?.email || '', [Validators.required, Validators.email]],
      firstname: [contact?.firstname || '', Validators.required],
      lastname: [contact?.lastname || '', Validators.required],
      phone: [contact?.phone || '']
    });
  }

  setSelectedValues(values: any[]): void {
    this.hubspotPreferencesSelected = values;
  }

  toggleSubscription(pref: any) {
    pref.selected = !pref.selected;
    this.hubspotContactService
      .updateCommunicationPreference(
        this.contactForm.get('email').value,
        pref.id,
        pref.selected ? 'SUBSCRIBED' : 'UNSUBSCRIBED'
      )
      .subscribe({
              next: () => {
                this.gungNotificationService.notify('DONE', '','success');
              },
              error: error => {
                console.log('error',error );
                this.gungNotificationService.notify('ERROR', error.error.message,'error');
                pref.selected = !pref.selected;
              }
            });
  }

  save({ target }): void {
    if (this.contactForm.valid) {
      gungAddRemoveSpinner(target);
      this.contactForm.value;

      if (!this.editContact) {
        const payload = {
          contact: {
            properties: this.contactForm.value
          },
          communicationSubscriptionIds: [...this.hubspotPreferencesSelected, ...this.defaultHubspotPreferences]
        };
        this.hubspotContactService
          .createHubspotContact(this.customerId, payload)
          .pipe(first())
          .subscribe({
            next: contact => {
              gungAddRemoveSpinner(target);
              this.delegate.modalRef.get(this.componentId).close({});
            },
            error: err => {
              gungAddRemoveSpinner(target);
              this.gungNotificationService.notify(
                'Error',
                'A contact with this email already exists',
                'error'
              );
            }
          });
      } else {
        const payload = {
          ...this.editContact,
          extra: {
            ...this.editContact.extra,
            hubspotContact: {
              ...this.editContact.extra.hubspotContact,
              properties: {
                ...this.editContact.extra.hubspotContact.properties,
                ...this.contactForm.value
              }
            }
          }
        };
        this.hubspotContactService
          .editHubspotContact(this.customerId, payload)
          .pipe(first())
          .subscribe(contact => {
            gungAddRemoveSpinner(target);
            this.delegate.modalRef.get(this.componentId).close({});
          });
      }
    } else {
      this.contactForm.markAllAsTouched();
      return;
    }
  }
}
